import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiContext } from '@shared/contexts';
import { TItem, TItemType } from '@modules/items';
import { addItemsToFolderQueryData } from '../items/api/itemQueryData';
import { getMasterFromData, TReplicaVersionMode, TUseReplicaResult } from './types';
import { TERMINOLOGY } from '@shared/i18n';

// --- GET:

export const useReplica = (id: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const getReplica = async (): Promise<TUseReplicaResult> => {
    const data: TUseReplicaResult = (await apiClient.get(`/replicas/${id}`)).data;
    getMasterFromData(data.masterData).versions.reverse();
    return data;
  };

  const { data, isError, error } = useQuery({
    retry,
    queryKey: [`replica-${id}`],
    queryFn: getReplica,
  });
  useHandleQueryError(
    isError,
    error,
    `Failed to load ${TERMINOLOGY.replica.lower} ${id}!`,
    `Loading ${TERMINOLOGY.replica.lower} failed!`
  );

  return { error, data };
};

// --- POST:

type TCreateReplicaItem = {
  _id: string;
  itemType: Extract<TItemType, 'DOCUMENT' | 'FILE'>;
  versionMode: TReplicaVersionMode;
  versionId?: string;
};

export const useCreate = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { parent: string; items: TCreateReplicaItem[] }): Promise<TItem[]> => {
    const res = await apiClient.post(`/replicas`, variables);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (items: TItem[], variables) => {
      show('success', `${items.length === 1 ? TERMINOLOGY.replica.upper : TERMINOLOGY.replica.upperPlural} created.`);
      addItemsToFolderQueryData(queryClient, variables.parent, items);
    },
    onError: handleApiError(`Creating ${TERMINOLOGY.replica.lower} failed!`),
  });
};

// --- PUT:

export const useUpdate = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: {
    _id: string;
    versionMode: TReplicaVersionMode;
    versionId?: string;
  }): Promise<TUseReplicaResult> => {
    const data: TUseReplicaResult = (
      await apiClient.put(`/replicas/${variables._id}`, {
        versionMode: variables.versionMode,
        versionId: variables.versionId,
      })
    ).data;
    getMasterFromData(data.masterData).versions.reverse();
    return data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (newData: TUseReplicaResult, variables) => {
      show('success', `${TERMINOLOGY.replica.upper} updated.`);
      queryClient.setQueryData([`replica-${variables._id}`], (oldData: TUseReplicaResult | undefined) => newData);
    },
    onError: handleApiError(`Updating ${TERMINOLOGY.replica.lower} failed!`),
  });
};
